import { useCallback } from "react";

/**
 * A hook to track student usage of AI agents
 */
const useAgentTracker = () => {
  // Helper to get the API base URL (without /api suffix for agent-usage routes)
  const getApiBaseUrl = () => {
    const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000";
    // Remove /api suffix if present, as agent-usage routes are at root level
    return baseUrl.replace(/\/api$/, "");
  };

  // Helper to get auth token
  const getAuthToken = () => {
    return (
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Create a new agent usage session
  const startAgentSession = useCallback(
    async (agentType, subject = null, topic = null) => {
      try {
        const studentId = localStorage.getItem("student_id");
        const userId = localStorage.getItem("user_id");

        if (!studentId) {
          console.warn("No student_id found - skipping agent tracking");
          return null;
        }

        const token = getAuthToken();

        if (!token) {
          console.warn("No authentication token - skipping agent tracking");
          return null;
        }

        const response = await fetch(`${getApiBaseUrl()}/agent-usage/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            student_id: studentId,
            user_id: userId,
            agent_type: agentType,
            subject,
            topic,
            session_start: new Date().toISOString(),
            interactions: [], // Initialize empty interactions array
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Failed to start agent session:", errorText);
          return null; // Return null instead of throwing to prevent breaking UI
        }

        const data = await response.json();
        console.log("✅ Agent session started:", data._id);
        return data._id; // Return the MongoDB ObjectId
      } catch (error) {
        console.error("Failed to start agent session:", error);
        return null; // Graceful degradation
      }
    },
    []
  );

  // Record an interaction within a session
  const recordInteraction = useCallback(
    async (sessionId, query, response, durationMs = null, feedback = null) => {
      if (!sessionId) {
        console.warn("No session ID - skipping interaction tracking");
        return false;
      }

      try {
        const token = getAuthToken();

        if (!token) {
          console.warn("No authentication token - skipping interaction tracking");
          return false;
        }

        const durationSec = durationMs ? durationMs / 1000 : null;

        const interactionData = {
          interaction_type: "chat", // Add interaction type
          timestamp: new Date().toISOString(),
          query,
          response,
          duration: durationSec,
          feedback_rating: feedback?.rating,
          feedback_comment: feedback?.comment,
        };

        const apiResponse = await fetch(
          `${getApiBaseUrl()}/agent-usage/${sessionId}/interaction`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(interactionData),
          }
        );

        if (!apiResponse.ok) {
          const errorText = await apiResponse.text();
          console.error("Failed to record interaction:", errorText);
          return false; // Don't throw - graceful degradation
        }

        const data = await apiResponse.json();
        console.log("✅ Interaction recorded");
        return true;
      } catch (error) {
        console.error("Failed to record agent interaction:", error);
        return false; // Graceful degradation
      }
    },
    []
  );

  // End a session
  const endAgentSession = useCallback(async (sessionId) => {
    if (!sessionId) {
      console.warn("No session ID - skipping session end");
      return false;
    }

    try {
      const token = getAuthToken();

      if (!token) {
        console.warn("No authentication token - skipping session end");
        return false;
      }

      const response = await fetch(
        `${getApiBaseUrl()}/agent-usage/${sessionId}/end`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to end agent session:", errorText);
        return false;
      }

      console.log("✅ Agent session ended");
      return true;
    } catch (error) {
      console.error("Failed to end agent session:", error);
      return false;
    }
  }, []);

  return {
    startAgentSession,
    recordInteraction,
    endAgentSession,
  };
};

export default useAgentTracker;